// Wave 20.47c — aero.chat cheer glyphs, rich edition.
//
// Custom-designed branded reactions. Each one uses gradients +
// layered shapes so it reads like a real, polished emoji rather
// than a flat icon — but the palette + silhouettes are ours, and
// they look the same on every OS (no Unicode drift).
//
// Every glyph exposes `size` and (optionally) `color` — color is
// ignored for glyphs that own their palette via gradient (most).
// The `id` prop lets a page mount multiple copies without SVG
// gradient-id collisions.

import React from 'react';

interface Props {
  size?: number;
  color?: string;
  /** Namespace suffix for gradient IDs. Defaults to a random-ish
   *  runtime value; pass a stable id if you memoize the component. */
  id?: string;
}

let seq = 0;
function useGlyphId(prefix: string, override?: string): string {
  const [n] = React.useState(() => ++seq);
  return override ?? `${prefix}-${n}`;
}

// -----------------------------------------------------------------
// Aero Bolt — thick lightning with green→teal gradient + shine.
// -----------------------------------------------------------------
export function AeroBoltGlyph({ size = 24, id }: Props) {
  const g = useGlyphId('cg-aero', id);
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <defs>
        <linearGradient id={`${g}-fill`} x1="8" y1="0" x2="24" y2="32" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#B7FF9A" />
          <stop offset="0.55" stopColor="#6AFF45" />
          <stop offset="1" stopColor="#3AAE20" />
        </linearGradient>
      </defs>
      <path
        d="M19 2 L6 17 h6 l-3 13 L26 13 h-6 l3 -11 z"
        fill={`url(#${g}-fill)`}
        stroke="#0a100c"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M18 4 L10 15 h3 l-1 4"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

// -----------------------------------------------------------------
// Wind Streak — three sky-blue swirls, tapered, with soft outline.
// -----------------------------------------------------------------
export function WindStreakGlyph({ size = 24, id }: Props) {
  const g = useGlyphId('cg-wind', id);
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <defs>
        <linearGradient id={`${g}-a`} x1="0" y1="8" x2="32" y2="8" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#7EEBFF" stopOpacity="0" />
          <stop offset="0.15" stopColor="#7EEBFF" />
          <stop offset="1" stopColor="#B4F5FF" />
        </linearGradient>
        <linearGradient id={`${g}-b`} x1="0" y1="16" x2="32" y2="16" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#5AC7E5" stopOpacity="0" />
          <stop offset="0.1" stopColor="#5AC7E5" />
          <stop offset="1" stopColor="#7EEBFF" />
        </linearGradient>
      </defs>
      <path d="M2 9 h14 a4 4 0 1 0 -4 -4" stroke={`url(#${g}-a)`} strokeWidth="3.4" strokeLinecap="round" fill="none" />
      <path d="M2 17 h20 a4 4 0 1 1 -4 4" stroke={`url(#${g}-b)`} strokeWidth="3.4" strokeLinecap="round" fill="none" />
      <path d="M2 24 h12 a3 3 0 1 0 -3 -3" stroke={`url(#${g}-a)`} strokeWidth="2.6" strokeLinecap="round" fill="none" opacity="0.75" />
    </svg>
  );
}

// -----------------------------------------------------------------
// Summit — twin snow-capped peaks with sun disc.
// -----------------------------------------------------------------
export function SummitGlyph({ size = 24, id }: Props) {
  const g = useGlyphId('cg-summit', id);
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <defs>
        <linearGradient id={`${g}-sky`} x1="16" y1="0" x2="16" y2="20" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFCA6A" />
          <stop offset="1" stopColor="#FF7EB2" />
        </linearGradient>
        <linearGradient id={`${g}-rock`} x1="16" y1="8" x2="16" y2="30" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#8ED676" />
          <stop offset="1" stopColor="#2A6E1C" />
        </linearGradient>
      </defs>
      <circle cx="24" cy="10" r="4" fill={`url(#${g}-sky)`} />
      <path d="M2 28 L11 8 L17 20 L21 14 L30 28 Z" fill={`url(#${g}-rock)`} stroke="#0a100c" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8 20 L11 8 L14 20 Z" fill="#F5FFF0" opacity="0.85" />
      <path d="M19 18 L21 14 L23 18 Z" fill="#F5FFF0" opacity="0.85" />
    </svg>
  );
}

// -----------------------------------------------------------------
// Cadence — brand-green disc wheel with spokes + hub.
// -----------------------------------------------------------------
export function CadenceGlyph({ size = 24, id }: Props) {
  const g = useGlyphId('cg-cad', id);
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <defs>
        <radialGradient id={`${g}-hub`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#B7FF9A" />
          <stop offset="1" stopColor="#3AAE20" />
        </radialGradient>
      </defs>
      <circle cx="16" cy="16" r="13" fill="#0a100c" />
      <circle cx="16" cy="16" r="13" fill="none" stroke="#6AFF45" strokeWidth="2.4" />
      <g stroke="#6AFF45" strokeWidth="1.6" strokeLinecap="round">
        <line x1="16" y1="4" x2="16" y2="28" />
        <line x1="4" y1="16" x2="28" y2="16" />
        <line x1="7.5" y1="7.5" x2="24.5" y2="24.5" />
        <line x1="7.5" y1="24.5" x2="24.5" y2="7.5" />
      </g>
      <circle cx="16" cy="16" r="3" fill={`url(#${g}-hub)`} stroke="#0a100c" strokeWidth="1" />
    </svg>
  );
}

// -----------------------------------------------------------------
// Watts — power dial with a hot-orange needle and red-zone arc.
// -----------------------------------------------------------------
export function WattsGlyph({ size = 24, id }: Props) {
  const g = useGlyphId('cg-watts', id);
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <defs>
        <linearGradient id={`${g}-arc`} x1="0" y1="16" x2="32" y2="16" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#6AFF45" />
          <stop offset="0.55" stopColor="#FFD86A" />
          <stop offset="1" stopColor="#FF5A3C" />
        </linearGradient>
      </defs>
      <path d="M4 22 A12 12 0 0 1 28 22" stroke="#0a100c" strokeWidth="6" strokeLinecap="round" />
      <path d="M4 22 A12 12 0 0 1 28 22" stroke={`url(#${g}-arc)`} strokeWidth="3.5" strokeLinecap="round" />
      <line x1="16" y1="22" x2="23" y2="9" stroke="#FF7A3C" strokeWidth="2.6" strokeLinecap="round" />
      <circle cx="16" cy="22" r="2.6" fill="#FFCA6A" stroke="#0a100c" strokeWidth="1" />
    </svg>
  );
}

// -----------------------------------------------------------------
// Focus — target crosshair with hot green pulse.
// -----------------------------------------------------------------
export function FocusGlyph({ size = 24, id }: Props) {
  const g = useGlyphId('cg-focus', id);
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <defs>
        <radialGradient id={`${g}-core`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="0.4" stopColor="#B7FF9A" />
          <stop offset="1" stopColor="#3AAE20" />
        </radialGradient>
      </defs>
      <circle cx="16" cy="16" r="13" fill="none" stroke="#6AFF45" strokeWidth="2.2" />
      <circle cx="16" cy="16" r="8" fill="none" stroke="#6AFF45" strokeWidth="1.8" opacity="0.85" />
      <circle cx="16" cy="16" r="4" fill={`url(#${g}-core)`} />
      <g stroke="#6AFF45" strokeWidth="2" strokeLinecap="round">
        <line x1="16" y1="1" x2="16" y2="5" />
        <line x1="16" y1="27" x2="16" y2="31" />
        <line x1="1" y1="16" x2="5" y2="16" />
        <line x1="27" y1="16" x2="31" y2="16" />
      </g>
    </svg>
  );
}

// -----------------------------------------------------------------
// Draft — three trailing chevrons with alpha ramp.
// -----------------------------------------------------------------
export function DraftGlyph({ size = 24, id }: Props) {
  const g = useGlyphId('cg-draft', id);
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <defs>
        <linearGradient id={`${g}-c`} x1="0" y1="16" x2="32" y2="16" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#C7B3FF" stopOpacity="0.35" />
          <stop offset="1" stopColor="#FFCA6A" />
        </linearGradient>
      </defs>
      <polyline points="4,6 12,16 4,26"  fill="none" stroke={`url(#${g}-c)`} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.45" />
      <polyline points="12,6 20,16 12,26" fill="none" stroke={`url(#${g}-c)`} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.75" />
      <polyline points="20,6 28,16 20,26" fill="none" stroke={`url(#${g}-c)`} strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// -----------------------------------------------------------------
// GOAT — full goat head with horns + beard, cream body + gold horns.
// -----------------------------------------------------------------
export function GoatGlyph({ size = 24, id }: Props) {
  const g = useGlyphId('cg-goat', id);
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <defs>
        <linearGradient id={`${g}-fur`} x1="16" y1="4" x2="16" y2="30" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#F5EED1" />
          <stop offset="1" stopColor="#B99A55" />
        </linearGradient>
        <linearGradient id={`${g}-horn`} x1="16" y1="0" x2="16" y2="10" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFEBA0" />
          <stop offset="1" stopColor="#B48317" />
        </linearGradient>
      </defs>
      {/* Horns */}
      <path d="M11 5 C 8 3 5.5 2.5 3.5 4 C 5 6 7.5 7.5 10 7.8" fill={`url(#${g}-horn)`} stroke="#0a100c" strokeWidth="1" strokeLinejoin="round" />
      <path d="M21 5 C 24 3 26.5 2.5 28.5 4 C 27 6 24.5 7.5 22 7.8" fill={`url(#${g}-horn)`} stroke="#0a100c" strokeWidth="1" strokeLinejoin="round" />
      {/* Head + beard */}
      <path
        d="M16 5 C 11 5 8 8 8 13 v3 c0 2.5 1.2 4.2 3.4 5 l-1 4 h3 l0.8 -2.6 h3.6 l0.8 2.6 h3 l-1 -4 c2.2 -0.8 3.4 -2.5 3.4 -5 v-3 c0 -5 -3 -8 -8 -8 z"
        fill={`url(#${g}-fur)`}
        stroke="#0a100c"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      {/* Snout highlight */}
      <path d="M13 15 C 14 17 18 17 19 15" stroke="#0a100c" strokeWidth="1" fill="none" strokeLinecap="round" />
      {/* Eyes */}
      <ellipse cx="12" cy="12.5" rx="1.1" ry="1.5" fill="#0a100c" />
      <ellipse cx="20" cy="12.5" rx="1.1" ry="1.5" fill="#0a100c" />
      <circle cx="12.3" cy="12" r="0.45" fill="#F5EED1" />
      <circle cx="20.3" cy="12" r="0.45" fill="#F5EED1" />
    </svg>
  );
}
